import { z } from 'zod';
import { TenantSlugSchema } from './tenant';

const HexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?$/, 'must be a hex color like #0066cc');

const FontName = z
  .string()
  .regex(
    /^[A-Za-z][A-Za-z0-9 ]*(?:-[A-Za-z0-9][A-Za-z0-9 ]*)*$/,
    'font name must start with a letter and contain only letters, digits, spaces, and internal hyphens',
  )
  .max(80);

const MediaUrl = z
  .string()
  .min(1)
  .refine(
    (v) => v.startsWith('/') || v.startsWith('http://') || v.startsWith('https://'),
    'media URL must be root-relative (/...) or absolute (http(s)://...)',
  );

export const MediaRefSchema = z.object({
  url: MediaUrl,
  alt: z.string().default(''),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});
export type MediaRef = z.infer<typeof MediaRefSchema>;

export const LinkSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  openInNewTab: z.boolean().optional(),
});
export type Link = z.infer<typeof LinkSchema>;

export const HeroBlockSchema = z.object({
  blockType: z.literal('hero'),
  headline: z.string().min(1),
  subheadline: z.string().optional(),
  image: MediaRefSchema.optional(),
  cta: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
    })
    .optional(),
  alignment: z.enum(['left', 'center']).default('center'),
  style: z.enum(['default', 'fullbleed', 'split']).default('default'),
});
export type HeroBlock = z.infer<typeof HeroBlockSchema>;

export const FeaturesBlockSchema = z.object({
  blockType: z.literal('features'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  columns: z.enum(['2', '3', '4']).default('3'),
  variant: z.enum(['card', 'numbered']).default('card'),
  display: z.enum(['cards', 'list', 'icon-row']).default('cards'),
  items: z
    .array(
      z.object({
        icon: MediaRefSchema.optional(),
        title: z.string().min(1),
        description: z.string().optional(),
      }),
    )
    .min(1)
    .max(8),
});
export type FeaturesBlock = z.infer<typeof FeaturesBlockSchema>;

export const AboutBlockSchema = z.object({
  blockType: z.literal('about'),
  heading: z.string().optional(),
  body: z.string().optional(),
  image: MediaRefSchema.optional(),
  imagePosition: z.enum(['left', 'right']).default('right'),
});
export type AboutBlock = z.infer<typeof AboutBlockSchema>;

export const CTABlockSchema = z.object({
  blockType: z.literal('cta'),
  heading: z.string().min(1),
  subtext: z.string().optional(),
  variant: z.enum(['standard', 'phone-primary', 'two-column', 'form-only']).default('standard'),
  phone: z
    .string()
    .max(40)
    .regex(/^[0-9+\-()\s]+$/, 'phone: digits and + - ( ) spaces only')
    .refine((v) => /\d/.test(v), 'phone must contain at least one digit')
    .optional(),
  formSlug: z.string().min(1).optional(),
  primary: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
      openInNewTab: z.boolean().optional(),
    })
    .optional(),
  secondary: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
      openInNewTab: z.boolean().optional(),
    })
    .optional(),
  backgroundImage: MediaRefSchema.optional(),
});
export type CTABlock = z.infer<typeof CTABlockSchema>;

// --- Medical theme blocks ----------------------------------------------------
// Five domain-specific blocks for the `medical` theme (clinics, dental, etc.).
// They're added to the discriminated union below so any tenant — regardless of
// theme — can opt into them, but the medical builder theme is the one that
// renders them visually. Theme strategy: themed blocks live alongside generic
// ones rather than being scoped per theme, because Payload's blocks field is
// a flat list and per-theme block scoping is deferred to a future session.

const PhoneNumber = z
  .string()
  .min(1)
  .max(40)
  .regex(
    /^[0-9+\-()\s]+$/,
    'phone number must contain only digits and the symbols + - ( ) and spaces',
  )
  .refine((v) => /\d/.test(v), 'phone number must contain at least one digit');

export const ClinicWeekdaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'holiday',
]);
export type ClinicWeekday = z.infer<typeof ClinicWeekdaySchema>;

export const DepartmentsBlockSchema = z.object({
  blockType: z.literal('departments'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  items: z
    .array(
      z.object({
        name: z.string().min(1),
        description: z.string().optional(),
        symptoms: z.string().optional(),
        href: z.string().optional(),
      }),
    )
    .min(1)
    .max(12),
});
export type DepartmentsBlock = z.infer<typeof DepartmentsBlockSchema>;

export const DoctorProfileBlockSchema = z.object({
  blockType: z.literal('doctorProfile'),
  heading: z.string().optional(),
  doctors: z
    .array(
      z.object({
        name: z.string().min(1),
        nameReading: z.string().optional(),
        title: z.string().optional(),
        specialty: z.string().optional(),
        photo: MediaRefSchema.optional(),
        biography: z.string().optional(),
        career: z.array(z.string().min(1)).default([]),
        qualifications: z.array(z.string().min(1)).default([]),
      }),
    )
    .min(1)
    .max(8),
});
export type DoctorProfileBlock = z.infer<typeof DoctorProfileBlockSchema>;

export const ClinicHoursBlockSchema = z.object({
  blockType: z.literal('clinicHours'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  schedule: z
    .array(
      z.object({
        day: ClinicWeekdaySchema,
        morning: z.string().optional(),
        afternoon: z.string().optional(),
        note: z.string().optional(),
      }),
    )
    .min(1)
    .max(8)
    .refine(
      (rows) => new Set(rows.map((r) => r.day)).size === rows.length,
      'each weekday may only appear once in the schedule',
    ),
  closedNote: z.string().optional(),
  phone: PhoneNumber.optional(),
  phoneHours: z.string().optional(),
  reservationHref: z.string().optional(),
});
export type ClinicHoursBlock = z.infer<typeof ClinicHoursBlockSchema>;

export const AccessBlockSchema = z.object({
  blockType: z.literal('access'),
  heading: z.string().optional(),
  address: z.string().min(1),
  postalCode: z.string().optional(),
  nearestStations: z
    .array(
      z.object({
        line: z.string().min(1),
        station: z.string().min(1),
        walkingMinutes: z.number().int().nonnegative().optional(),
      }),
    )
    .max(6)
    .default([]),
  parking: z.string().optional(),
  busInfo: z.string().optional(),
  mapEmbedUrl: z
    .string()
    .url()
    .refine(
      (u) => /^https:\/\/(www\.)?google\.(com|co\.jp)\/maps\/embed/.test(u),
      'mapEmbedUrl must be a Google Maps embed URL (https://www.google.com/maps/embed?... or https://www.google.co.jp/maps/embed?...)',
    )
    .optional(),
  staticMapImage: MediaRefSchema.optional(),
});
export type AccessBlock = z.infer<typeof AccessBlockSchema>;

export const NewsBlockSchema = z.object({
  blockType: z.literal('news'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  items: z
    .array(
      z.object({
        publishedAt: z.string().min(1),
        category: z.enum(['notice', 'closure', 'announcement']).default('notice'),
        title: z.string().min(1),
        body: z.string().optional(),
        href: z.string().optional(),
      }),
    )
    .min(1)
    .max(20),
});
export type NewsBlock = z.infer<typeof NewsBlockSchema>;

export const FooterSocialPlatformSchema = z.enum(['x', 'instagram', 'facebook', 'line', 'youtube']);
export type FooterSocialPlatform = z.infer<typeof FooterSocialPlatformSchema>;

export const FooterBlockSchema = z.object({
  blockType: z.literal('footer'),
  companyName: z.string().optional(),
  address: z.string().optional(),
  links: z.array(LinkSchema).default([]),
  social: z
    .array(
      z.object({
        platform: FooterSocialPlatformSchema,
        href: z.string().min(1),
      }),
    )
    .default([]),
  copyright: z.string().optional(),
});
export type FooterBlock = z.infer<typeof FooterBlockSchema>;

// Restaurant theme blocks (S2-11). Live in the shared union so the same
// PageBlock type can carry payloads for any theme — each theme's renderer
// only branches on the block types it supports.

export const MenuListItemSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  priceJpy: z.number().int().nonnegative().optional(),
  priceLabel: z.string().min(1).optional(),
  image: MediaRefSchema.optional(),
  /**
   * Free-form allergen tags. We deliberately do not enumerate values: even
   * the消費者庁 specific-7 list expanded recently and the operator's actual
   * menu may use friendlier terms (`卵`, `小麦`). Storing as strings keeps
   * editing low-friction; renderers display them verbatim.
   */
  allergens: z.array(z.string().min(1)).default([]),
  isRecommended: z.boolean().default(false),
});
export type MenuListItem = z.infer<typeof MenuListItemSchema>;

export const MenuListCategorySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  items: z.array(MenuListItemSchema).min(1).max(40),
});
export type MenuListCategory = z.infer<typeof MenuListCategorySchema>;

export const MenuListBlockSchema = z.object({
  blockType: z.literal('menu_list'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  categories: z.array(MenuListCategorySchema).min(1).max(12),
  /**
   * 注記。アレルゲン表示の免責、写真とのイメージ差異など、料金表の下に
   * 1 行で出したい内容を入れる。
   */
  footnote: z.string().optional(),
});
export type MenuListBlock = z.infer<typeof MenuListBlockSchema>;

export const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
  'holiday',
] as const;
export const WeekdayKeySchema = z.enum(WEEKDAY_KEYS);
export type WeekdayKey = z.infer<typeof WeekdayKeySchema>;

export const BusinessHoursDaySchema = z.object({
  day: WeekdayKeySchema,
  /**
   * Empty array = closed. Multiple ranges support split shifts (e.g. lunch
   * 11:00–14:00 + dinner 17:30–22:00).
   */
  ranges: z
    .array(
      z
        .object({
          // Lenient HH:MM (24h) match — operators may write `9:00` rather than `09:00`.
          // Hour range 0–23; minute range 00–59. `\d|1\d|2[0-3]` accepts single- or
          // double-digit hours without requiring a leading zero.
          open: z
            .string()
            .regex(
              /^(?:[01]\d|2[0-3]|\d):[0-5]\d$/,
              'open must be HH:MM (24h, e.g. 9:00 or 17:30)',
            ),
          close: z
            .string()
            .regex(
              /^(?:[01]\d|2[0-3]|\d):[0-5]\d$/,
              'close must be HH:MM (24h, e.g. 9:00 or 17:30)',
            ),
          /** Optional label such as `ランチ` / `ディナー` for split shifts. */
          label: z.string().min(1).optional(),
        })
        .refine(
          (r) => {
            const toMin = (t: string) => {
              const [h = 0, m = 0] = t.split(':').map(Number);
              return h * 60 + m;
            };
            return toMin(r.close) > toMin(r.open);
          },
          // Ranges spanning midnight (e.g. 22:00–02:00) are not supported in v1.
          // For venues open past midnight, use 23:59 as the closing time.
          {
            message:
              'close must be later than open (overnight ranges spanning midnight are not supported)',
          },
        ),
    )
    .default([]),
  note: z.string().min(1).optional(),
});
export type BusinessHoursDay = z.infer<typeof BusinessHoursDaySchema>;

export const BusinessHoursBlockSchema = z.object({
  blockType: z.literal('business_hours'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  days: z
    .array(BusinessHoursDaySchema)
    .min(1)
    .max(8)
    .superRefine((days, ctx) => {
      const seen = new Set<string>();
      days.forEach((d, i) => {
        if (seen.has(d.day)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `duplicate day: ${d.day}`,
            path: [i, 'day'],
          });
        }
        seen.add(d.day);
      });
    }),
  /** Free-form note rendered below the table — e.g. 年末年始の特別営業. */
  note: z.string().min(1).optional(),
});
export type BusinessHoursBlock = z.infer<typeof BusinessHoursBlockSchema>;

export const ReservationBlockSchema = z.object({
  blockType: z.literal('reservation'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  /**
   * Slug of a `FormDefinition` in the same `TenantContent.forms` array.
   * Renderers resolve the form lazily so a missing slug doesn't break
   * builds; instead we surface a noindex placeholder.
   */
  formSlug: z.string().min(1).optional(),
  /** Phone number for direct reservations (tel: link). */
  phone: z.string().optional(),
  /** External reservation system URL (タベログ, ホットペッパー, etc.) — also allows a site-relative path. */
  externalUrl: z
    .string()
    .min(1)
    .refine(
      (v) => v.startsWith('https://') || v.startsWith('/'),
      'externalUrl must be an absolute https:// URL or a site-relative path starting with /',
    )
    .optional(),
  /** Pre-form policy text (キャンセル料、人数上限など). */
  policy: z.string().optional(),
});
export type ReservationBlock = z.infer<typeof ReservationBlockSchema>;

export const RestaurantAccessBlockSchema = z.object({
  blockType: z.literal('restaurant_access'),
  heading: z.string().optional(),
  /** Postal address rendered verbatim (with newlines preserved). */
  address: z.string().optional(),
  /** Optional pre-formatted directions / nearest-station info. */
  directions: z.string().optional(),
  parkingInfo: z.string().optional(),
  /**
   * Embed source for an `<iframe>` map (Google Maps, OpenStreetMap, etc.).
   * Renderers must validate this is an https URL before injecting it; we
   * only accept absolute https and reject `javascript:` etc.
   */
  mapEmbedUrl: z
    .string()
    .url()
    .refine(
      (v) => v.startsWith('https://'),
      'mapEmbedUrl must be an https:// URL — http and other schemes are rejected to keep the iframe safe',
    )
    .optional(),
  mapEmbedTitle: z.string().optional(),
});
export type RestaurantAccessBlock = z.infer<typeof RestaurantAccessBlockSchema>;

// Law theme blocks (S2-14). Domain-specific blocks for 士業 (law offices,
// tax accountants, administrative scriveners, etc.). Same flat-union strategy
// as medical/restaurant.

export const OfficeProfileBlockSchema = z.object({
  blockType: z.literal('officeProfile'),
  heading: z.string().optional(),
  officeName: z.string().min(1),
  representativeName: z.string().optional(),
  representativeTitle: z.string().optional(),
  representativePhoto: MediaRefSchema.optional(),
  greeting: z.string().optional(),
  registrationNumbers: z
    .array(
      z.object({
        label: z.string().min(1),
        value: z.string().min(1),
      }),
    )
    .default([]),
  affiliations: z.array(z.string().min(1)).default([]),
  career: z.array(z.string().min(1)).default([]),
});
export type OfficeProfileBlock = z.infer<typeof OfficeProfileBlockSchema>;

export const PracticeItemSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  priceLabel: z.string().optional(),
  href: z.string().optional(),
});
export type PracticeItem = z.infer<typeof PracticeItemSchema>;

export const PracticeBlockSchema = z.object({
  blockType: z.literal('practice'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  variant: z.enum(['grid', 'list', 'accordion']).default('grid'),
  items: z.array(PracticeItemSchema).min(1).max(16),
  footnote: z.string().optional(),
});
export type PracticeBlock = z.infer<typeof PracticeBlockSchema>;

export const CaseItemSchema = z.object({
  title: z.string().min(1),
  category: z.string().optional(),
  overview: z.string().optional(),
  result: z.string().optional(),
  clientLabel: z.string().optional(),
});
export type CaseItem = z.infer<typeof CaseItemSchema>;

export const CasesBlockSchema = z.object({
  blockType: z.literal('cases'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  variant: z.enum(['card', 'metrics']).default('card'),
  items: z.array(CaseItemSchema).min(1).max(20),
  disclaimer: z.string().optional(),
});
export type CasesBlock = z.infer<typeof CasesBlockSchema>;

export const ConsultationCTABlockSchema = z.object({
  blockType: z.literal('consultationCTA'),
  heading: z.string().min(1),
  subtext: z.string().optional(),
  variant: z.enum(['centered', 'split']).default('centered'),
  formSlug: z.string().min(1).optional(),
  phone: z.string().optional(),
  priceNote: z.string().optional(),
  primary: z
    .object({
      label: z.string().min(1),
      href: z.string().min(1),
    })
    .optional(),
});
export type ConsultationCTABlock = z.infer<typeof ConsultationCTABlockSchema>;

export const LawNewsItemSchema = z.object({
  publishedAt: z.string().min(1),
  category: z.enum(['column', 'legal_update', 'notice']).default('notice'),
  title: z.string().min(1),
  body: z.string().optional(),
  href: z.string().optional(),
});
export type LawNewsItem = z.infer<typeof LawNewsItemSchema>;

export const LawNewsBlockSchema = z.object({
  blockType: z.literal('lawNews'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  items: z.array(LawNewsItemSchema).min(1).max(20),
});
export type LawNewsBlock = z.infer<typeof LawNewsBlockSchema>;

// Beauty theme blocks (S2-12). Like restaurant and medical, these live on the
// shared union: the `beauty` Astro theme renders them, but storing them on
// `PageBlock` keeps Payload's flat blocks field uniform across themes.

export const ServiceMenuItemSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  /** Duration in minutes — drives the badge label and the totalDuration aggregate. */
  durationMinutes: z.number().int().positive().max(720).optional(),
  /** Tax-included price in JPY. Renderers prefer `priceLabel` if both are set. */
  priceJpy: z.number().int().nonnegative().optional(),
  /**
   * Free-form price label — used when `priceJpy` cannot express the price
   * (例: 「コースにより異なる」「8,000円〜」). Empty string is rejected to
   * avoid swallowing a forgotten priceJpy entry into a blank cell.
   */
  priceLabel: z.string().min(1).optional(),
  isRecommended: z.boolean().default(false),
});
export type ServiceMenuItem = z.infer<typeof ServiceMenuItemSchema>;

export const ServiceMenuCategorySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  items: z.array(ServiceMenuItemSchema).min(1).max(40),
});
export type ServiceMenuCategory = z.infer<typeof ServiceMenuCategorySchema>;

export const ServiceMenuBlockSchema = z.object({
  blockType: z.literal('service_menu'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  display: z.enum(['table', 'cards', 'comparison']).default('table'),
  categories: z.array(ServiceMenuCategorySchema).min(1).max(12),
  /** 表の下に出す注記（税込/サービス料/指名料 等の免責表記）。 */
  footnote: z.string().optional(),
});
export type ServiceMenuBlock = z.infer<typeof ServiceMenuBlockSchema>;

/** Shared href validator used for staff booking, social, and external-booking
 * links. Allows absolute https:// URLs and **single-slash** site-relative paths.
 * Rejects:
 *  - http:// (non-secure)
 *  - javascript:, data:, and other schemes (XSS)
 *  - protocol-relative `//evil.example` (would otherwise pass `startsWith('/')`
 *    and silently leak users to an arbitrary host) */
const SafeHref = z
  .string()
  .min(1)
  .refine(
    (v) => v.startsWith('https://') || (v.startsWith('/') && !v.startsWith('//')),
    'href must start with https:// or / (site-relative); protocol-relative // is rejected',
  );

export const StaffProfileItemSchema = z.object({
  name: z.string().min(1),
  nameReading: z.string().optional(),
  /** 役職・肩書き（例: 店長 / トップスタイリスト / アイデザイナー）。 */
  title: z.string().optional(),
  /** 得意な施術カテゴリ（例: カラー / 縮毛矯正）。 */
  specialties: z.array(z.string().min(1)).default([]),
  photo: MediaRefSchema.optional(),
  bio: z.string().optional(),
  /**
   * 指名予約用の URL — 外部予約システムの直リンクを想定。
   * `javascript:` や `data:` スキームを拒否します。
   */
  bookingHref: SafeHref.optional(),
  /**
   * 任意の SNS 1 件（例: Instagram）。
   * `javascript:` や `data:` スキームを拒否します。
   */
  socialHref: SafeHref.optional(),
});
export type StaffProfileItem = z.infer<typeof StaffProfileItemSchema>;

export const StaffProfileBlockSchema = z.object({
  blockType: z.literal('staff_profile'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  staff: z.array(StaffProfileItemSchema).min(1).max(12),
});
export type StaffProfileBlock = z.infer<typeof StaffProfileBlockSchema>;

export const GalleryItemSchema = z.object({
  /** Optional caption — e.g. 「ハイライトカラー / 所要 90 分」 */
  caption: z.string().optional(),
  /** Optional "before" image for paired Before/After layout. */
  before: MediaRefSchema.optional(),
  /** Required "after" / result image. */
  after: MediaRefSchema,
  /** どのスタイリスト・施術メニューの作例か（任意）。 */
  staffName: z.string().optional(),
  serviceName: z.string().optional(),
});
export type GalleryItem = z.infer<typeof GalleryItemSchema>;

export const GalleryBlockSchema = z.object({
  blockType: z.literal('gallery'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  /**
   * `pairs` renders before/after side-by-side; `single` shows just the result
   * image. `pairs` is the default because it is the dominant beauty-industry
   * pattern, but salons that only photograph finished styling should still
   * have a usable layout.
   */
  layout: z.enum(['pairs', 'single', 'grid', 'masonry', 'carousel']).default('pairs'),
  display: z.enum(['grid', 'masonry', 'carousel']).optional(),
  items: z.array(GalleryItemSchema).min(1).max(24),
  /**
   * 注記：景品表示法・薬機法の観点から、効果保証や治療効果の暗示を避ける
   * 注釈をつける運用想定。デフォルト文字列は seed が用意する。
   */
  footnote: z.string().optional(),
});
export type GalleryBlock = z.infer<typeof GalleryBlockSchema>;

// `BookingCTABlockSchema` deliberately stays a plain ZodObject so it can sit
// in `PageBlockSchema`'s discriminated union — `discriminatedUnion` rejects
// ZodEffects (the result of `.superRefine` / `.refine` on the outer object).
// Cross-field channel-presence checks ("must have at least one of phone /
// formSlug / externalUrl") are enforced at the call site via
// `bookingCTAHasChannel` rather than inside the schema. The renderer also
// degrades gracefully — see `apps/builder/themes/beauty/components/BookingCTA.astro`.
export const BookingCTABlockSchema = z.object({
  blockType: z.literal('booking_cta'),
  ctaPattern: z
    .enum(['standard', 'phone-primary', 'external-primary', 'form-only', 'two-column'])
    .default('standard'),
  accentMessage: z.string().optional(),
  heading: z.string().optional(),
  intro: z.string().optional(),
  /** Slug of a `FormDefinition` to embed inline (内部問い合わせフォーム)。 */
  formSlug: z.string().min(1).optional(),
  /** 予約 / 電話の補足（例: 「24 時間オンライン受付」）。 */
  note: z.string().optional(),
  /** 電話番号 — `tel:` リンクとして展開する。 */
  phone: PhoneNumber.optional(),
  /**
   * 外部予約システムへのリンク（ホットペッパービューティー等）。サイト内パスも
   * 受け付ける（先頭 `/`）。`http://` や `//evil.example` のようなプロトコル
   * 相対 URL もビルド時に拒否（`SafeHref` を共有）。
   */
  externalUrl: SafeHref.optional(),
  /** 外部予約サイトのラベル（例:「ホットペッパービューティーで予約」）。 */
  externalLabel: z.string().min(1).optional(),
  /** キャンセルポリシーや指名料の案内など、フォーム上部に出したい注記。 */
  policy: z.string().optional(),
});
export type BookingCTABlock = z.infer<typeof BookingCTABlockSchema>;

/**
 * True when the booking CTA exposes at least one bookable channel (form,
 * phone, or external URL). `formSlug` is treated as available even before
 * the matching `FormDefinition` is resolved — the renderer falls back to a
 * "form not yet ready" notice when the slug has no matching form.
 */
export function bookingCTAHasChannel(block: BookingCTABlock): boolean {
  // PhoneNumber already enforces at least one digit, so a validated
  // BookingCTABlock.phone is never spaces-only. The .trim() is a defensive
  // guard for any caller that bypasses schema validation (e.g. tests,
  // migration scripts). It also keeps this helper consistent with the
  // renderer's rawPhone trimming logic.
  return Boolean(block.formSlug || block.phone?.trim() || block.externalUrl);
}

export const BeforeAfterSliderItemSchema = z.object({
  before: MediaRefSchema,
  after: MediaRefSchema,
  caption: z.string().optional(),
  serviceName: z.string().optional(),
});
export type BeforeAfterSliderItem = z.infer<typeof BeforeAfterSliderItemSchema>;

export const BeforeAfterSliderBlockSchema = z.object({
  blockType: z.literal('before_after_slider'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  items: z.array(BeforeAfterSliderItemSchema).min(1).max(8),
  footnote: z.string().optional(),
});
export type BeforeAfterSliderBlock = z.infer<typeof BeforeAfterSliderBlockSchema>;

// Retail theme blocks (S2-15). Targeted at brick-and-mortar retailers and
// small EC operators who funnel out to BASE / Shopify / STORES rather than
// running a full storefront. Same union-shape pattern as the restaurant
// blocks: each theme renderer only branches on the block types it supports.

export const ProductGridItemSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  /**
   * Display price as integer JPY (税込 expected). Renderers format with
   * Intl.NumberFormat — operators wanting "¥ 1,200〜" or "OPEN PRICE" should
   * use `priceLabel` instead.
   */
  priceJpy: z.number().int().nonnegative().optional(),
  priceLabel: z.string().min(1).optional(),
  image: MediaRefSchema.optional(),
  /**
   * Stock state. Free-form to avoid pinning operators to a specific inventory
   * vocabulary; renderers display as a small badge. Common values: `在庫あり`,
   * `残りわずか`, `予約受付中`, `完売`.
   */
  stockLabel: z.string().min(1).optional(),
  /**
   * External link target — typically a deep-link into the operator's BASE /
   * Shopify / STORES product page. We only accept https:// or site-relative
   * (`/...`) values to keep the rendered card safe. Protocol-relative URLs
   * (`//evil.example`) are rejected because browsers resolve them as
   * cross-origin links under the page's scheme — they look path-like but are
   * not site-relative.
   */
  href: z
    .string()
    .min(1)
    .refine(
      (v) => v.startsWith('https://') || (v.startsWith('/') && !v.startsWith('//')),
      'href must be an absolute https:// URL or a site-relative path starting with / (protocol-relative // URLs are rejected)',
    )
    .optional(),
  isFeatured: z.boolean().default(false),
});
export type ProductGridItem = z.infer<typeof ProductGridItemSchema>;

export const ProductGridBlockSchema = z.object({
  blockType: z.literal('product_grid'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  display: z.enum(['grid', 'masonry', 'featured', 'list']).default('grid'),
  columns: z.enum(['2', '3', '4']).default('3'),
  items: z.array(ProductGridItemSchema).min(1).max(48),
  footnote: z.string().optional(),
});
export type ProductGridBlock = z.infer<typeof ProductGridBlockSchema>;

const StoreInfoHoursLineSchema = z
  .object({
    /** Free-form day label — supports patterns like `月-金`, `土日祝`, `毎日`. */
    days: z.string().min(1),
    /** Free-form hours range — `10:00-19:00`, `定休` etc. Renderers do not parse. */
    hours: z.string().min(1),
  })
  .strict();

export const StoreInfoStoreSchema = z.object({
  name: z.string().min(1),
  address: z.string().optional(),
  phone: z.string().optional(),
  /**
   * Hours rendered as a compact list of `<dt>days</dt><dd>hours</dd>` rows.
   * Free-form so operators can write `月〜金 10:00–19:00 / 土日 11:00–18:00`
   * without forcing per-weekday entries — retail hours change less often
   * than restaurants and the value here is mostly informational.
   */
  hours: z.array(StoreInfoHoursLineSchema).min(1).max(8),
  /** 定休日の自由記述。例: `毎週水曜・第 2 火曜` */
  holiday: z.string().optional(),
  /** Optional note rendered below the hours table. */
  note: z.string().optional(),
});
export type StoreInfoStore = z.infer<typeof StoreInfoStoreSchema>;

export const StoreInfoBlockSchema = z.object({
  blockType: z.literal('store_info'),
  heading: z.string().optional(),
  intro: z.string().optional(),
  /**
   * One row per physical store. Multi-store support: at least one is
   * required, but the renderer lays out 1〜N stores in a responsive grid.
   * `min(1)` keeps the block useful; `max(12)` is a guardrail that matches
   * the typical small-retailer footprint.
   */
  stores: z.array(StoreInfoStoreSchema).min(1).max(12),
});
export type StoreInfoBlock = z.infer<typeof StoreInfoBlockSchema>;

export const OnlineShopCTABlockSchema = z.object({
  blockType: z.literal('online_shop_cta'),
  layout: z.enum(['centered', 'banner', 'minimal']).default('centered'),
  heading: z.string().min(1),
  subtext: z.string().optional(),
  /**
   * EC-platform branding. We accept the major Japanese SaaS ECs by name so
   * the renderer can show a recognisable label / icon. `other` is the
   * escape hatch for self-hosted shops or platforms we don't recognise.
   */
  platform: z.enum(['base', 'shopify', 'stores', 'rakuten', 'amazon', 'other']).default('other'),
  /**
   * Destination URL. Required because the whole point of the block is to
   * funnel out. https-only to avoid `javascript:` and to keep mixed content
   * out of the page.
   */
  href: z
    .string()
    .min(1)
    .refine(
      (v) => v.startsWith('https://'),
      'href must be an absolute https:// URL — the EC link is the whole point of this block',
    ),
  ctaLabel: z.string().min(1).default('オンラインショップへ'),
  /**
   * Optional secondary link (e.g. a category page). Same scheme rules as
   * `href`, plus site-relative paths are accepted for in-site secondary
   * destinations.
   */
  secondaryHref: z
    .string()
    .min(1)
    .refine(
      (v) => v.startsWith('https://') || (v.startsWith('/') && !v.startsWith('//')),
      'secondaryHref must be https:// or site-relative (/...) — protocol-relative // URLs are rejected',
    )
    .optional(),
  secondaryLabel: z.string().min(1).optional(),
});
export type OnlineShopCTABlock = z.infer<typeof OnlineShopCTABlockSchema>;

export const PromoBlockSchema = z.object({
  blockType: z.literal('promo'),
  layout: z.enum(['card', 'full-width', 'text-only']).default('card'),
  heading: z.string().min(1),
  body: z.string().optional(),
  /**
   * Display style. `sale` warms toward the secondary color (campaign banner);
   * `announcement` uses a neutral surface (新商品入荷など). Defaults to
   * `announcement` so an under-specified block reads as informational.
   */
  variant: z.enum(['sale', 'announcement']).default('announcement'),
  /**
   * Optional promotion window — both ISO date strings (YYYY-MM-DD).
   * Renderers display the range verbatim; they do not auto-hide expired
   * promos because operators generally want to keep promotional pages live
   * until they manually swap to the next one.
   */
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be ISO date YYYY-MM-DD')
    .optional(),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'endDate must be ISO date YYYY-MM-DD')
    .optional(),
  cta: z
    .object({
      label: z.string().min(1),
      href: z
        .string()
        .min(1)
        .refine(
          (v) => v.startsWith('https://') || (v.startsWith('/') && !v.startsWith('//')),
          'cta.href must be https:// or site-relative (/...) — protocol-relative // URLs are rejected',
        ),
    })
    .optional(),
  image: MediaRefSchema.optional(),
});
export type PromoBlock = z.infer<typeof PromoBlockSchema>;

export const RetailAccessNearestStationSchema = z.object({
  line: z.string().min(1),
  station: z.string().min(1),
  walkingMinutes: z.number().int().nonnegative().optional(),
  exit: z.string().optional(),
});
export type RetailAccessNearestStation = z.infer<typeof RetailAccessNearestStationSchema>;

export const RetailAccessBlockSchema = z.object({
  blockType: z.literal('retail_access'),
  heading: z.string().optional(),
  address: z.string().optional(),
  nearestStations: z.array(RetailAccessNearestStationSchema).max(6).default([]),
  parkingInfo: z.string().optional(),
  /**
   * Embed source for an `<iframe>` map. Same defense-in-depth as
   * `restaurant_access`: only https:// is accepted at the schema layer and
   * the renderer re-validates before injecting.
   */
  mapEmbedUrl: z
    .string()
    .url()
    .refine(
      (v) => v.startsWith('https://'),
      'mapEmbedUrl must be an https:// URL — http and other schemes are rejected to keep the iframe safe',
    )
    .optional(),
  mapEmbedTitle: z.string().optional(),
});
export type RetailAccessBlock = z.infer<typeof RetailAccessBlockSchema>;

export const PageBlockSchema = z.discriminatedUnion('blockType', [
  HeroBlockSchema,
  FeaturesBlockSchema,
  AboutBlockSchema,
  CTABlockSchema,
  FooterBlockSchema,
  DepartmentsBlockSchema,
  DoctorProfileBlockSchema,
  ClinicHoursBlockSchema,
  AccessBlockSchema,
  NewsBlockSchema,
  MenuListBlockSchema,
  BusinessHoursBlockSchema,
  ReservationBlockSchema,
  RestaurantAccessBlockSchema,
  ServiceMenuBlockSchema,
  StaffProfileBlockSchema,
  GalleryBlockSchema,
  BookingCTABlockSchema,
  BeforeAfterSliderBlockSchema,
  ProductGridBlockSchema,
  StoreInfoBlockSchema,
  OnlineShopCTABlockSchema,
  PromoBlockSchema,
  RetailAccessBlockSchema,
  OfficeProfileBlockSchema,
  PracticeBlockSchema,
  CasesBlockSchema,
  ConsultationCTABlockSchema,
  LawNewsBlockSchema,
]);
export type PageBlock = z.infer<typeof PageBlockSchema>;

export const ThemeCustomizationSchema = z.object({
  themeSlug: z.string().default('corporate'),
  themeVersion: z.string().default('v1.0.0'),
  primaryColor: HexColor.default('#0066cc'),
  secondaryColor: HexColor.default('#ff6600'),
  backgroundColor: HexColor.default('#ffffff'),
  textColor: HexColor.default('#111111'),
  fontHeading: FontName.default('Noto Sans JP'),
  fontBody: FontName.default('Noto Sans JP'),
  logo: MediaRefSchema.optional(),
  favicon: MediaRefSchema.optional(),
  customCss: z
    .string()
    .max(10000)
    .refine((v) => !/<\/style/i.test(v), 'customCss must not contain </style')
    .optional(),
});
export type ThemeCustomization = z.infer<typeof ThemeCustomizationSchema>;

export const NavigationItemSchema = z.object({
  label: z.string().min(1),
  href: z.string().min(1),
  openInNewTab: z.boolean().optional(),
});
export type NavigationItem = z.infer<typeof NavigationItemSchema>;

export const NavigationSchema = z.object({
  slug: z.enum(['header', 'footer', 'drawer']),
  items: z.array(NavigationItemSchema),
});
export type Navigation = z.infer<typeof NavigationSchema>;

export const PageSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  ogImage: MediaRefSchema.optional(),
  seoNoindex: z.boolean().default(false),
  blocks: z.array(PageBlockSchema),
});
export type Page = z.infer<typeof PageSchema>;

export const FormFieldSchema = z.object({
  name: z.string().min(1),
  label: z.string().min(1),
  type: z.enum(['text', 'email', 'tel', 'textarea', 'select']),
  required: z.boolean().default(false),
  placeholder: z.string().optional(),
  options: z.array(z.object({ label: z.string().min(1), value: z.string().min(1) })).optional(),
});
export type FormField = z.infer<typeof FormFieldSchema>;

export const FormDefinitionSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  fields: z
    .array(FormFieldSchema)
    .min(1, 'form must have at least one field')
    .superRefine((fields, ctx) => {
      fields.forEach((field, i) => {
        if (field.type === 'select' && (!field.options || field.options.length === 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'select field must have at least one option',
            path: [i, 'options'],
          });
        }
        if (field.type !== 'select' && field.options && field.options.length > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `options are only valid on select fields (got type '${field.type}')`,
            path: [i, 'options'],
          });
        }
      });
    }),
  submitButtonLabel: z.string().default('送信'),
  turnstileRequired: z.boolean().default(true),
});
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;

export const TenantContentSchema = z.object({
  tenant: z.object({
    id: z.string().uuid(),
    slug: TenantSlugSchema,
    name: z.string().min(1),
    googleAnalyticsMeasurementId: z
      .string()
      .regex(/^G-[A-Z0-9]{4,20}$/, 'invalid Google Analytics measurement ID')
      .optional(),
  }),
  customization: ThemeCustomizationSchema,
  navigations: z.object({
    header: z.array(NavigationItemSchema).default([]),
    footer: z.array(NavigationItemSchema).default([]),
    drawer: z.array(NavigationItemSchema).default([]),
  }),
  pages: z
    .array(PageSchema)
    .min(1, 'tenant content must have at least one page')
    .refine((pages) => new Set(pages.map((p) => p.slug)).size === pages.length, {
      message: 'page slugs must be unique',
    }),
  forms: z
    .array(FormDefinitionSchema)
    .refine((forms) => new Set(forms.map((f) => f.slug)).size === forms.length, {
      message: 'form slugs must be unique',
    })
    .default([]),
});
export type TenantContent = z.infer<typeof TenantContentSchema>;
